import type { HeaderContext, Row, SortingState } from '@tanstack/react-table';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { Ellipsis } from 'common/ui/Ellipsis';
import { Stack } from 'common/ui/Layout';
import { useMemo } from 'react';
import { type NavigateFunction } from 'react-router-dom';
import { Colors } from 'styles';
import { toLocaleString } from 'utils/date';
import { HEADER_BASE_LENGTH, ICON_COLOR } from './constants';
import { getRightDrawerClonePath, getRightDrawerEditPath } from './paths';
import {
  RightDrawerAction,
  RightDrawerTab,
  type RightDrawerTableCellProps,
  type RightDrawerTableItemType,
  type RightDrawerTabType,
} from './types';
import { RightDrawerActionIcon } from './RightDrawerActionIcon';
import {
  RIGHT_DRAWER_TOOLTIP_ANCHOR_STYLE,
  ScrollableTooltip,
} from './RightDrawerScrollableTooltip';
import { RightDrawerTableSortableColumnHeader } from './RightDrawerTableSortableColumnHeader';

const INSERT_COLUMN_WIDTH = 24;
const ACTION_COLUMN_SINGLE_SIZE = HEADER_BASE_LENGTH + 2;
const ACTION_COLUMN_DUAL_SIZE = ACTION_COLUMN_SINGLE_SIZE * 2;

type UseRightDrawerTableColumnsArgs = {
  activeTab: RightDrawerTabType;
  basePath: string;
  priorsSuffix: string;
  isFocusModeOn: boolean;
  hasClinicLevelTemplatePermission: boolean;
  navigate: NavigateFunction;
  starredId: Nullable<string>;
  onSetSorting: (sortingState: SortingState) => void;
  isInsertColumnEnabled: boolean;
  onRowDoubleClick: (item: RightDrawerTableItemType) => Promise<void>;
  getRowAction: (item: RightDrawerTableItemType) => RightDrawerTableItemType;
};

type RightDrawerCellContext = RightDrawerTableCellProps & {
  row: Row<RightDrawerTableItemType>;
};

export const useRightDrawerTableColumns = ({
  activeTab,
  basePath,
  priorsSuffix,
  isFocusModeOn,
  hasClinicLevelTemplatePermission,
  navigate,
  starredId,
  onSetSorting,
  isInsertColumnEnabled,
  onRowDoubleClick,
  getRowAction,
}: UseRightDrawerTableColumnsArgs) => {
  return useMemo(() => {
    const actionColumn = {
      header: ' ',
      accessorKey: 'action',
      size: hasClinicLevelTemplatePermission ? ACTION_COLUMN_DUAL_SIZE : ACTION_COLUMN_SINGLE_SIZE,
      cell: ({ row }: RightDrawerCellContext) => {
        if (row.original.id === '') return <></>;

        const isOwnerOrHasClinicLevelPermission =
          (row.original.owner != null && row.original.id != null) ||
          hasClinicLevelTemplatePermission;
        const editURL = isOwnerOrHasClinicLevelPermission
          ? getRightDrawerEditPath({
              basePath,
              activeTab,
              itemId: row.original.id,
              priorsSuffix,
            })
          : null;

        const isClinicLevel = row.original.owner == null;
        const cloneURL = isClinicLevel
          ? getRightDrawerClonePath({
              basePath,
              activeTab,
              itemId: row.original.id,
            })
          : null;

        return (
          <div data-testid={`reporter-${activeTab}-id-row`}>
            {(isOwnerOrHasClinicLevelPermission || isClinicLevel) && (
              <span
                data-testid={`reporter-${activeTab}-actions-${row.original.id}`}
                css={`
                  display: inline-flex;
                  align-items: center;
                  gap: 2px;
                  white-space: nowrap;
                  margin-top: 2px;
                  color: ${Colors.gray10};
                  &:focus svg {
                    opacity: 1;
                  }
                `}
              >
                {isOwnerOrHasClinicLevelPermission && (
                  <RightDrawerActionIcon
                    dataTestId={`reporter-${activeTab}-edit-button`}
                    dataTestValue={row.original.name}
                    type={RightDrawerAction.Edit}
                    onClick={
                      editURL
                        ? () => {
                            navigate(editURL);
                          }
                        : undefined
                    }
                    iconStyle={{ marginTop: -2 }}
                  />
                )}
                {isClinicLevel && (
                  <RightDrawerActionIcon
                    dataTestId={`reporter-${activeTab}-clone-button`}
                    dataTestValue={row.original.name}
                    type={RightDrawerAction.Clone}
                    onClick={
                      cloneURL
                        ? () => {
                            navigate(cloneURL);
                          }
                        : undefined
                    }
                    iconStyle={{ marginTop: -2 }}
                  />
                )}
              </span>
            )}
          </div>
        );
      },
    };

    const nameColumn = {
      id: 'name',
      header: (headerContext: HeaderContext<RightDrawerTableItemType, unknown>) => (
        <RightDrawerTableSortableColumnHeader
          headerContext={headerContext}
          label="Name"
          onSetSorting={onSetSorting}
        />
      ),
      accessorKey: 'name',
      cell: ({ row }: RightDrawerCellContext) => <Ellipsis>{row.original.name}</Ellipsis>,
      size: 20,
      enableSorting: true,
    };

    const lastUsedColumn = {
      id: 'lastUsed',
      header: (headerContext: HeaderContext<RightDrawerTableItemType, unknown>) => (
        <RightDrawerTableSortableColumnHeader
          headerContext={headerContext}
          label="Last Used"
          onSetSorting={onSetSorting}
        />
      ),
      accessorKey: 'lastUsedDateTime',
      cell: ({ row }: RightDrawerCellContext) => (
        <Ellipsis>
          {row.original.lastUsedDateTime != null ? (
            toLocaleString(row.original.lastUsedDateTime, { year: '2-digit' })
          ) : (
            <span style={{ color: Colors.gray6 }}>-</span>
          )}
        </Ellipsis>
      ),
      size: 12,
      enableSorting: true,
    };

    const clinicColumn = {
      header: () => (
        <Tooltip title={`Clinic ${activeTab === RightDrawerTab.Template ? 'Templates' : 'Macros'}`}>
          <BusinessIcon
            css={`
              font-size: 14px;
              margin: 3px 0px 0px 0px;
              color: rgba(236, 236, 236, 0.56);
            `}
          />
        </Tooltip>
      ),
      accessorKey: 'id',
      cell: ({ row, isVirtualScrolling }: RightDrawerCellContext) => {
        if (row.original.id === '') {
          return <span />;
        }

        if (starredId === row.original.id) {
          const defaultIcon = (
            <Stack alignX="start" alignY="center" data-testid="default-autoload-template-icon">
              <StarIcon
                css={`
                  font-size: 14px;
                  color: ${ICON_COLOR};
                `}
              />
            </Stack>
          );

          if (isVirtualScrolling) {
            return <span style={RIGHT_DRAWER_TOOLTIP_ANCHOR_STYLE}>{defaultIcon}</span>;
          }

          return (
            <ScrollableTooltip
              title={activeTab === RightDrawerTab.Template ? 'Default Template' : 'Default Macro'}
            >
              {defaultIcon}
            </ScrollableTooltip>
          );
        }

        if (row.original.owner != null) {
          return null;
        }

        const clinicIcon = (
          <Stack
            alignX="start"
            alignY="center"
            data-testid={isVirtualScrolling ? 'clinic-placeholder-icon' : undefined}
          >
            <BusinessIcon
              css={`
                font-size: 14px;
                color: ${ICON_COLOR};
              `}
            />
          </Stack>
        );

        if (isVirtualScrolling) {
          return <span style={RIGHT_DRAWER_TOOLTIP_ANCHOR_STYLE}>{clinicIcon}</span>;
        }

        return (
          <ScrollableTooltip
            title={activeTab === RightDrawerTab.Template ? 'Clinic Template' : 'Clinic Macro'}
          >
            {clinicIcon}
          </ScrollableTooltip>
        );
      },
      size: 2,
    };

    if (!isInsertColumnEnabled) {
      return [actionColumn, nameColumn, lastUsedColumn, clinicColumn];
    }

    const insertColumn = {
      header: ' ',
      accessorKey: 'insert',
      size: INSERT_COLUMN_WIDTH,
      minSize: INSERT_COLUMN_WIDTH,
      maxSize: INSERT_COLUMN_WIDTH,
      meta: {
        disableFlexGrow: true,
      },
      cell: ({ row, isVirtualScrolling }: RightDrawerCellContext) => {
        if (row.original.id === '' || isFocusModeOn) return <></>;

        const insertIcon = (
          <AddIcon
            className="insertIcon"
            data-testid={
              isVirtualScrolling ? `reporter-${activeTab}-insert-placeholder-icon` : undefined
            }
            css={`
              border-radius: 2px;
              background-color: ${Colors.mainBlue};
              color: ${Colors.white};
            `}
          />
        );

        return (
          <div data-testid={`reporter-${activeTab}-insert-row`}>
            <span
              css={`
                display: flex;
                margin-top: 2px;
                color: ${Colors.gray10};
                &:focus svg {
                  opacity: 1;
                }
              `}
            >
              {isVirtualScrolling ? (
                insertIcon
              ) : (
                <IconButton
                  data-testid={`reporter-${activeTab}-insert-button`}
                  data-testvalue={row.original.name}
                  size="small"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void onRowDoubleClick(getRowAction(row.original));
                  }}
                  sx={{
                    color: Colors.gray10,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 0,
                  }}
                >
                  <Tooltip title="Insert">{insertIcon}</Tooltip>
                </IconButton>
              )}
            </span>
          </div>
        );
      },
    };

    return [insertColumn, nameColumn, lastUsedColumn, actionColumn, clinicColumn];
  }, [
    activeTab,
    isFocusModeOn,
    hasClinicLevelTemplatePermission,
    basePath,
    priorsSuffix,
    navigate,
    starredId,
    onSetSorting,
    isInsertColumnEnabled,
    onRowDoubleClick,
    getRowAction,
  ]);
};
